import { useMemo, useState } from 'react';
import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderTitle,
} from '@customerio/pluma-components/react';
import { PeopleTable, ATTRIBUTE_COLUMN_PREFIX, attributeColumnId } from '@/features/people';
import { LoadingPage } from '@/components/page/loading';
import { useIsRedacted } from '@/services/permissions/hooks';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { usePeopleListPage, usePeopleSelection, PER_PAGE } from './hooks';
import { AddPeopleButton } from './components/add-people-button';
import { PeopleSearchSlot } from './components/people-search-slot';
import { PeopleWelcome } from './components/people-welcome';

// Always present as fixed columns, so never offered as attribute columns.
const HIDDEN_ATTRIBUTES = new Set(['created_at', 'email']);

/** The default visible attribute column until the user configures their own. */
const DEFAULT_COLUMNS: readonly string[] = ['id'];

/**
 * Attribute-column state for the list. The visible set is in-memory for now —
 * per-user persistence (`people_page.columns`) and the paged attribute
 * catalog land with the column picker (FPS-2613).
 */
function usePeopleColumns(sentAttributes: readonly string[]) {
  const [selectedColumns, setSelectedColumns] = useState<readonly string[]>(DEFAULT_COLUMNS);

  const allAttributes = useMemo(
    () => [...new Set(sentAttributes)].filter((attribute) => !HIDDEN_ATTRIBUTES.has(attribute)),
    [sentAttributes],
  );

  const columnVisibility = useMemo(() => {
    const selected = new Set(selectedColumns);
    const visibility: Record<string, boolean> = {};
    for (const attribute of allAttributes) {
      visibility[attributeColumnId(attribute)] = selected.has(attribute);
    }
    return visibility;
  }, [allAttributes, selectedColumns]);

  const onColumnVisibilityChange = (visibility: Record<string, boolean>) => {
    setSelectedColumns(
      Object.entries(visibility)
        .filter(([columnId, visible]) => visible && columnId.startsWith(ATTRIBUTE_COLUMN_PREFIX))
        .map(([columnId]) => columnId.slice(ATTRIBUTE_COLUMN_PREFIX.length)),
    );
  };

  return { allAttributes, columnVisibility, onColumnVisibilityChange };
}

export default function PeopleListPage() {
  const taxonomy = useProductTaxonomy();
  const state = usePeopleListPage();
  const selection = usePeopleSelection({
    people: state.people,
    totalCount: state.totalCount,
    dataStamp: state.listQuery.dataUpdatedAt,
  });

  const environment = useEnvironmentOptional().data?.environment;
  const columns = usePeopleColumns(environment?.sent_attributes ?? []);

  const isRedacted = useIsRedacted();
  const shouldRedactCreatedAt = isRedacted && environment?.is_sample_workspace !== true;

  // The add flow is still served by the embedded app; Ember redirects the
  // segment to the account's canonical people/profiles form itself.
  const addPeopleHref = generatePath('/workspaces/:workspaceId/journeys/people/create', {
    workspaceId: state.workspaceId,
  });

  if (state.welcomeDecision == null) {
    return <LoadingPage />;
  }

  if (state.welcomeDecision === 'welcome') {
    return <PeopleWelcome addPeopleHref={addPeopleHref} />;
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderIconTile name="person" />
        <PageHeaderTitle>{taxonomy.t('profiles.pageTitleWithFormerName')}</PageHeaderTitle>
        <PageHeaderActions>
          <AddPeopleButton href={addPeopleHref} />
        </PageHeaderActions>
      </PageHeader>
      <PageBody>
        <PeopleTable
          people={state.people}
          isLoading={state.listQuery.isFetching}
          allAttributes={columns.allAttributes}
          columnVisibility={columns.columnVisibility}
          onColumnVisibilityChange={columns.onColumnVisibilityChange}
          pagination={{
            page: state.page,
            pageSize: PER_PAGE,
            totalItemCount: state.totalCount,
            onPageChange: state.setPage,
          }}
          canEdit={state.canEdit}
          rowSelection={selection.rowSelection}
          onRowSelectionChange={selection.onRowSelectionChange}
          allSelected={selection.allSelected}
          totalCount={state.totalCount}
          selectedCount={selection.selectedPeople.length}
          onClearSelection={selection.clearSelection}
          onSelectAll={selection.onSelectAll}
          selectAllDisabled={state.selectAllDisabled}
          noFiltersApplied={state.noFiltersApplied}
          shouldRedactCreatedAt={shouldRedactCreatedAt}
          hasFilters={state.hasFilters}
          hasHiddenResults={state.hasHiddenResults}
          email={state.email}
          message={state.message}
          searchSlot={
            <PeopleSearchSlot
              activeSearchBy={state.activeSearchBy}
              onActivateSearchBy={state.activateSearchBy}
              email={state.email}
              onSearch={state.search}
              filters={state.filters}
              onClearFilters={state.clearFilters}
            />
          }
          addPeopleAction={<AddPeopleButton href={addPeopleHref} />}
        />
      </PageBody>
    </Page>
  );
}
